/**
 * Product page gallery: crossfade between images instead of sliding.
 *
 * When the visitor picks another image with a thumbnail, a dot or the prev/next arrows, the
 * theme's slideshow (assets/slideshow.js) is told to jump there instantly, and a copy of the
 * outgoing image is laid over it and faded out while the incoming image fades in (opacity
 * only, ~0.35s). Swiping on touch screens keeps the native slide. Programmatic changes
 * (e.g. a variant switching the image) are left to the theme. Reduced motion: instant swap.
 */
const FADE_MS = 350;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {HTMLElement} slideshow the main gallery's <slideshow-component> */
function enhance(slideshow) {
  if (slideshow.dataset.fadeEnhanced) return;
  slideshow.dataset.fadeEnhanced = '';

  const originalSelect = slideshow.select;
  if (typeof originalSelect !== 'function') return;

  slideshow.select = function (input, event, options = {}) {
    const from = this.slides?.[this.current];
    if (!event || reducedMotion.matches || !from) return originalSelect.call(this, input, event, options);

    const ghost = makeGhost(this, from);
    const result = originalSelect.call(this, input, event, { ...options, animate: false });

    // The theme updates `current` synchronously; if nothing changed, drop the ghost.
    const to = this.slides?.[this.current];
    if (!ghost) return result;
    if (!to || to === from) {
      ghost.remove();
      return result;
    }

    const incoming = to.querySelector('.product-media-constraint-wrapper') ?? to;
    incoming.animate([{ opacity: 0 }, { opacity: 1 }], { duration: FADE_MS, easing: 'ease' });
    ghost
      .animate([{ opacity: 1 }, { opacity: 0 }], { duration: FADE_MS, easing: 'ease', fill: 'forwards' })
      .finished.finally(() => ghost.remove());
    return result;
  };
}

/**
 * A non-interactive copy of the outgoing slide's image, positioned exactly over it inside the
 * slideshow container (above the slides, below the arrows).
 * @param {HTMLElement} slideshow
 * @param {HTMLElement} slide
 */
function makeGhost(slideshow, slide) {
  const source = slide.querySelector('.product-media-constraint-wrapper') ?? slide;
  const host = slide.parentElement?.parentElement; // <slideshow-container>
  if (!host) return null;

  const hostRect = host.getBoundingClientRect();
  const rect = source.getBoundingClientRect();
  if (!rect.width) return null;

  // Wrapped in an element with the slide's classes so the gallery CSS (frame, radius, shadow)
  // applies to the copy exactly as to the original.
  const ghost = document.createElement('div');
  ghost.className = `${slide.className} product-gallery-fade-ghost`;
  ghost.setAttribute('aria-hidden', 'true');
  ghost.inert = true;
  const copy = /** @type {HTMLElement} */ (source.cloneNode(true));
  // Strip anything the theme's components would pick up (refs, ids, declarative handlers).
  for (const el of [copy, ...copy.querySelectorAll('*')]) {
    el.removeAttribute('ref');
    el.removeAttribute('id');
    for (const attr of [...el.attributes]) if (attr.name.startsWith('on:')) el.removeAttribute(attr.name);
  }
  copy.style.width = `${rect.width}px`;
  copy.style.height = `${rect.height}px`;
  ghost.append(copy);

  Object.assign(ghost.style, {
    position: 'absolute',
    left: `${rect.left - hostRect.left}px`,
    top: `${rect.top - hostRect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
    padding: '0',
    aspectRatio: 'auto',
    background: 'transparent',
    pointerEvents: 'none',
    zIndex: '1',
  });

  if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
  const arrows = host.querySelector(':scope > slideshow-arrows');
  if (arrows) arrows.before(ghost);
  else host.append(ghost);
  return ghost;
}

function init() {
  document
    .querySelectorAll('.product-information media-gallery slideshow-component:not(.dialog-zoomed-gallery *)')
    .forEach((el) => {
      // slideshow.js may not have upgraded the element yet.
      customElements.whenDefined('slideshow-component').then(() => enhance(/** @type {HTMLElement} */ (el)));
    });
}

init();
// Variant changes can morph the gallery markup; re-enhance any new slideshow element.
document.addEventListener('variant:update', () => requestAnimationFrame(init));
